import { c as _c } from "react/compiler-runtime";
import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { getWorkspaceMembersTableColumns, SEATS_COLUMN } from "@components/table/members/members-table-constants";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { MembersTable } from "@components/table/members/members-table";
import { getRenderedWorkspaceMembersSortBy, MemberHeaders } from "@components/table/members/members-table-utils";
import { MembersSort } from "@components/table/members/members-sort";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { InviteMemberToCompany } from "@pages/members/invite-member-to-company";
import { MainMemberDetails } from "@pages/members/main-member-details";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMemberBulkActions } from "@src/pages/members/use-company-member-bulk-actions";
import { isMembersSortBy } from "@custom-types/type-guards";
import { fetchedMembersFlagsSelector, fetchingMembersFlagsSelector, filteredCompanyMembersSelector } from "@store/members/members-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { getExportComponent, sortMembers } from "@utils/member-utils";
import { useEffect } from "react";
import { MembersMobileView } from "@pages/members/members-mobile-view";
import { resetTableState } from "@store/table/table-slice";
import { DEFAULT_WORKSPACE_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { doesPlanIncludeSeats } from "@utils/member-seats-utils";
import { useFetchMemberSeats } from "@hooks/use-fetch-member-seats";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMembersFilters } from "@components/table/members/members-filters/use-members-filters";
import { useDeselectHiddenMembers } from "@pages/group-details/use-deselect-hidden-members";

/**
 * The columns that are shown by default. The status and the seats are hidden until the user shows
 * them or filters by them, see FILTERED_COLUMN_FIELDS.
 */
const DEFAULT_VISIBLE_COLUMNS = ["user", "email", "role"];

/** The hidden by default columns that are shown for as long as the members are filtered by them */
const FILTERED_COLUMN_FIELDS = {
  [MemberHeaders.status]: "status",
  [MemberHeaders.seats]: SEATS_COLUMN
};

/** Listing all the members of a company in a table */
export function MembersPage() {
  const $ = _c(23);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    isLoading: areSeatsLoading
  } = useFetchMemberSeats();
  useCompanyMembers();
  const seatsLookup = useMemberSeatsLookup();
  const shouldShowSeats = doesPlanIncludeSeats(seatsLookup);
  const {
    canInviteUsersToCompany
  } = useHasUserValidRoleCompanyLevel();
  const currentUser = useAppSelector(currentUserSelector);
  const filteredCompanyMembers = useAppSelector(filteredCompanyMembersSelector);
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  const {
    hasFetchedWorkspaceMembers
  } = useAppSelector(fetchedMembersFlagsSelector);
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultSort: DEFAULT_WORKSPACE_MEMBERS_SORT_BY,
      isValidSort: isMembersSortBy
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    sortBy: requestedSortBy,
    setSortBy
  } = useSubjectsSortByQuery(t0);
  const sortBy = getRenderedWorkspaceMembersSortBy(requestedSortBy, shouldShowSeats);
  const sortedMembers = sortMembers(filteredCompanyMembers, sortBy, seatsLookup);
  const {
    filteredMembers,
    filterChips,
    filteredColumns
  } = useMembersFilters({
    members: sortedMembers,
    tableSubject: "workspace"
  });
  useDeselectHiddenMembers(filteredMembers);
  const isLoading = isFetchingCompanyMembers || areSeatsLoading;
  const isAloneInWorkspace = hasFetchedWorkspaceMembers && !debouncedSearchText.length && sortedMembers.every(member => member.identity === currentUser?.identity);
  const hasNoSearchResults = !isLoading && hasFetchedWorkspaceMembers && !!debouncedSearchText.length && sortedMembers.length === 0;
  const shouldShowEmptyPage = isAloneInWorkspace || hasNoSearchResults;
  const listedMembers = isAloneInWorkspace ? [] : filteredMembers;
  let t1;
  if ($[1] !== filteredMembers || $[2] !== selectedSdbCompany) {
    t1 = {
      members: filteredMembers,
      company: selectedSdbCompany
    };
    $[1] = filteredMembers;
    $[2] = selectedSdbCompany;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const buttonComponents = useCompanyMemberBulkActions(t1);
  let t2;
  let t3;
  if ($[4] !== dispatch) {
    t2 = () => () => {
      dispatch(resetTableState());
    };
    t3 = [dispatch];
    $[4] = dispatch;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const t4 = hasFetchedWorkspaceMembers && !isAloneInWorkspace && <MainMemberDetails />;
  const t5 = listedMembers.length;
  const t6 = !shouldShowEmptyPage && canInviteUsersToCompany && <InviteMemberToCompany buttonVariant="filled" />;
  const t7 = listedMembers.length > 0 ? filterChips : undefined;
  const t8 = listedMembers.length > 0 && <MembersSort sortBy={sortBy} setSortBy={setSortBy} tableSubject="workspace" hiddenFields={shouldShowSeats ? undefined : [SEATS_COLUMN]} />;
  let t9;
  if ($[7] !== buttonComponents.export || $[8] !== listedMembers.length) {
    t9 = getExportComponent(listedMembers.length, buttonComponents.export);
    $[7] = buttonComponents.export;
    $[8] = listedMembers.length;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== isLoading || $[11] !== listedMembers.length || $[12] !== t6 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t10 = <EntitiesToolbar toolbarEntityName="Member" numberOfRows={t5} isLoading={isLoading} mobileAction={t6} filterChips={t7} sortActions={t8} exportComponent={t9} />;
    $[10] = isLoading;
    $[11] = listedMembers.length;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = shouldShowEmptyPage && <MembersEmptyPage subtitle="All of your workspace members will be found here. You can add members to your workspace by inviting them." button={<InviteMemberToCompany buttonVariant="filled" />} />;
  const t12 = isScreenSmOrSmaller && !shouldShowEmptyPage && <MembersMobileView bulkActionButtons={buttonComponents} members={filteredMembers} isLoading={isLoading} tableSubject="workspace" sortBy={sortBy} />;
  const t13 = !isScreenSmOrSmaller && !shouldShowEmptyPage && <ColumnPreferencesProvider tableKey="workspace-members-table" columns={getWorkspaceMembersTableColumns(shouldShowSeats)} options={{
    nonConfigurableColumns: ["options"],
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    temporarilyVisibleColumns: filteredColumns.flatMap(_temp)
  }}><MembersTable companyId={companyId} members={filteredMembers} tableSubject="workspace" isLoading={isLoading} bulkActionButtons={buttonComponents} sortBy={sortBy} setSortBy={setSortBy} /></ColumnPreferencesProvider>;
  let t14;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12 || $[20] !== t13 || $[21] !== t4) {
    t14 = <>{t4}{t10}{t11}{t12}{t13}</>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t4;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}
function _temp(column) {
  return FILTERED_COLUMN_FIELDS[column] ?? [];
}